/**
 * PreviewPanel — Preview premium estilo Emergent/Lovable.
 *
 * Props:
 *   appSlug     — slug de la app a previsualizar
 *   autoStart   — arranca preview al montar (default false)
 *   onClose     — callback al cerrar (opcional)
 *   inline      — true = variante embebida en card (borde, sombra, altura fija)
 *
 * Features:
 *   - URL bar con dot pulsante LIVE
 *   - Stop preview button
 *   - Phone bezel en modo móvil
 *   - SSE live-reload events
 *   - Logs panel colapsable
 *   - Share URL, open in new tab, reload
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatError } from "../api";
import {
  ExternalLink, Copy, RefreshCw, ChevronDown, ChevronUp,
  Loader2, CheckCircle2, XCircle, Monitor, Smartphone, X, Square,
} from "lucide-react";

const STATUS_CONFIG = {
  idle:      { label: "Sin preview",  color: "#6B7280", Icon: Monitor },
  starting:  { label: "Iniciando...", color: "#D97706", Icon: Loader2,  spin: true },
  ready:     { label: "En vivo",      color: "#059669", Icon: CheckCircle2 },
  building:  { label: "Compilando...",color: "#D97706", Icon: Loader2,  spin: true },
  crashed:   { label: "Error",        color: "#DC2626", Icon: XCircle },
  error:     { label: "Error",        color: "#DC2626", Icon: XCircle },
  stopped:   { label: "Detenido",     color: "#6B7280", Icon: Monitor },
};

export default function PreviewPanel({ appSlug, autoStart = false, onClose, inline = false }) {
  const [status, setStatus]         = useState("idle");
  const [proxyUrl, setProxyUrl]     = useState("");
  const [shareUrl, setShareUrl]     = useState("");
  const [eventsUrl, setEventsUrl]   = useState("");
  const [logs, setLogs]             = useState([]);
  const [logsOpen, setLogsOpen]     = useState(false);
  const [mobile, setMobile]         = useState(false);
  const [copied, setCopied]         = useState(false);
  const [error, setError]           = useState("");
  const [reloadKey, setReloadKey]   = useState(0);   // only used for initial load / hard reset
  const [iframeReady, setIframeReady] = useState(false);
  const [reloading, setReloading]   = useState(false);  // P2: live-reload in progress
  const [reloadToast, setReloadToast] = useState("");    // P2: "↺ api.py changed"
  const [iframeError, setIframeError] = useState("");   // P4: friendly error when iframe shows JSON/blank
  const [startPct, setStartPct]     = useState(0);      // P5: 0-100 during "starting"
  const [startPhase, setStartPhase] = useState("");     // P5: human-readable phase label

  const iframeRef    = useRef(null);
  const evtSourceRef = useRef(null);
  const logsEndRef   = useRef(null);
  const started      = useRef(false);

  // ── SSE connection ─────────────────────────────────────────────────────────
  // P5: infer startup progress from log line content (declared before connectSSE)
  const inferStartProgress = useCallback((line) => {
    if (!line) return;
    const l = line.toLowerCase();
    const stages = [
      { match: ["creating venv", "python -m venv", "venv"],        pct: 10, label: "Creando entorno virtual..." },
      { match: ["pip install", "collecting", "downloading"],        pct: 30, label: "Instalando dependencias..." },
      { match: ["installing collected", "successfully installed"],  pct: 60, label: "Dependencias instaladas" },
      { match: ["uvicorn", "started server", "application startup"],pct: 80, label: "Arrancando servidor..." },
      { match: ["application startup complete", "running on"],      pct: 95, label: "Servidor listo" },
      { match: ["► preview listo", "✓ preview listo"],              pct: 100, label: "¡En vivo!" },
    ];
    for (const stage of stages) {
      if (stage.match.some(kw => l.includes(kw))) {
        setStartPct(p => Math.max(p, stage.pct));
        setStartPhase(stage.label);
        return;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectSSE = useCallback((url) => {
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
    }
    const es = new EventSource(url);
    evtSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "status") {
          setStatus(data.status);
          if (data.status === "ready") { setStartPct(100); setStartPhase("¡En vivo!"); }
        }
        if (data.type === "reload") {
          // P2: no-flash live reload
          // Keep iframe visible, show progress bar overlay, reload src directly
          const fileList = data.files?.slice(0, 3).join(", ");
          if (fileList) {
            addLog(`↺ Changed: ${fileList}`);
            setReloadToast(`↺ ${fileList}`);
            setTimeout(() => setReloadToast(""), 3000);
          }
          setReloading(true);
          setStatus("building");
          // 150ms settle — shorter than 400ms, visually faster
          setTimeout(() => {
            if (iframeRef.current) {
              // Change src directly on the DOM node — no React key change, no white flash
              const base = iframeRef.current.src.split("?")[0];
              iframeRef.current.src = base + "?_r=" + Date.now();
            } else {
              // Fallback: hard reload via key
              setReloadKey(k => k + 1);
              setIframeReady(false);
            }
            setStatus("ready");
          }, 150);
        }
        if (data.type === "log") {
          addLog(data.line);
          // P5: parse log line to infer startup progress
          inferStartProgress(data.line);
        }
      } catch (_) {}
    };

    es.onerror = () => {
      // SSE reconnects automatically after error
    };

    return es;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLog = (line) => {
    setLogs(prev => {
      const next = [...prev, line];
      return next.length > 200 ? next.slice(-200) : next;
    });
  };

  // P4: probe iframe content after load — detect JSON error / blank page
  const probeIframe = useCallback(async (src) => {
    if (!src) return;
    try {
      const res = await fetch(src, { method: "GET", credentials: "include" });
      if (!res.ok) {
        setIframeError(`Error ${res.status}: el servidor no respondió correctamente.`);
        return;
      }
      const text = await res.text();
      const trimmed = text.trim();
      // Detect raw JSON error bodies like {"detail":"..."}
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          const msg = parsed?.detail
            ? (Array.isArray(parsed.detail)
                ? parsed.detail.map(e => e.msg || String(e)).join(", ")
                : String(parsed.detail))
            : "El servidor devolvió un error inesperado.";
          setIframeError(msg);
          return;
        } catch (_) {}
      }
      // Detect blank / near-blank pages
      const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const bodyContent = bodyMatch ? bodyMatch[1].replace(/<[^>]+>/g, "").trim() : trimmed;
      if (bodyContent.length < 10) {
        setIframeError("La app cargó una página en blanco. Puede que aún esté iniciando.");
        return;
      }
      setIframeError(""); // all good
    } catch (_) {
      // fetch may fail cross-origin for some proxied content — don't block
    }
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logsOpen && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, logsOpen]);

  // ── Start preview ──────────────────────────────────────────────────────────
  const startPreview = useCallback(async () => {
    if (!appSlug) return;
    setStatus("starting");
    setError("");
    setIframeError("");
    setStartPct(5);
    setStartPhase("Iniciando...");
    setLogs([]);
    addLog("► Iniciando preview...");

    try {
      const { data } = await api.post(`/me/apps/${appSlug}/preview`);
      setProxyUrl(data.proxy_url);
      setShareUrl(data.share_url || "");
      setEventsUrl(data.events_url || "");
      setStatus(data.status || "ready");
      addLog(`✓ Preview listo en puerto ${data.port}`);
      if (data.share_url) addLog(`🔗 Share URL: ${data.share_url}`);
      if (data.events_url) connectSSE(data.events_url);
    } catch (e) {
      setStatus("error");
      const msg = formatError(e);
      setError(msg);
      addLog(`✗ Error: ${msg}`);
    }
  }, [appSlug, connectSSE]);

  // ── Stop preview ───────────────────────────────────────────────────────────
  const stopPreview = useCallback(async () => {
    if (!appSlug) return;
    try {
      await api.post(`/me/apps/${appSlug}/preview/stop`);
    } catch (_) {}
    if (evtSourceRef.current) { evtSourceRef.current.close(); evtSourceRef.current = null; }
    setStatus("stopped");
    setProxyUrl("");
    setShareUrl("");
    addLog("⏹ Preview detenido.");
  }, [appSlug]);

  // ── Auto-start — fires on mount AND whenever appSlug changes ─────────────
  // Intentionally avoids the started.current guard: POST /preview is idempotent
  // (reuses existing process, just refreshes the share token). This ensures we
  // always have a valid token even after a backend restart or tab re-focus.
  useEffect(() => {
    if (!autoStart || !appSlug) return;
    startPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSlug]);  // re-run only when the target app changes, not on every render

  // ── Cleanup SSE on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (evtSourceRef.current) evtSourceRef.current.close();
    };
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const canInteract = status === "ready" || status === "building";
  const isLive      = status === "ready";

  const copyShare = () => {
    const url = shareUrl ? `${window.location.origin}${shareUrl}` : `${window.location.origin}${proxyUrl}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const openExternal = () => {
    const url = shareUrl || proxyUrl;
    if (url) window.open(`${window.location.origin}${url}`, "_blank");
  };

  // Dot color for URL bar
  const dotClass = isLive ? "" : status === "starting" || status === "building"
    ? "preview-url-dot--starting" : status === "idle" || status === "stopped"
    ? "preview-url-dot--idle" : "preview-url-dot--error";

  // Live badge variant
  const badgeClass = isLive ? "" : (status === "starting" || status === "building")
    ? "preview-live-badge--starting" : "preview-live-badge--error";
  const badgeLabel = isLive ? "LIVE" : status === "starting" ? "STARTING" : status === "building" ? "BUILDING" : status === "stopped" ? "STOPPED" : status === "idle" ? "IDLE" : "ERROR";

  // Panel root class
  const panelClass = `preview-panel${inline ? " preview-panel--inline" : ""}`;

  return (
    <div className={panelClass} data-testid="preview-panel">

      {/* ── Header: status + app name + action buttons ──────────────────── */}
      <div className="preview-header">
        <div className="preview-status-row">
          {/* Live badge — replaces the old icon+text status */}
          <span className={`preview-live-badge ${badgeClass}`}>
            {(isLive || status === "starting" || status === "building") && (
              <span style={{
                width: 6, height: 6, borderRadius: "50%", display: "inline-block",
                background: "currentColor",
                animation: isLive ? "pulse-live 2s ease-in-out infinite" : "none",
              }} />
            )}
            {badgeLabel}
          </span>
          {appSlug && (
            <span className="preview-app-name">{appSlug}</span>
          )}
        </div>

        <div className="preview-actions">
          {/* Mobile/Desktop toggle */}
          <button className="preview-btn" onClick={() => setMobile(m => !m)}
                  title={mobile ? "Vista escritorio" : "Vista móvil"}>
            {mobile ? <Monitor size={14} /> : <Smartphone size={14} />}
          </button>

          {/* Reload */}
          {canInteract && (
            <button className="preview-btn"
                    onClick={() => { setReloadKey(k => k + 1); setIframeReady(false); }}
                    title="Recargar">
              <RefreshCw size={14} />
            </button>
          )}

          {/* Share */}
          {(shareUrl || proxyUrl) && canInteract && (
            <button className={`preview-btn${copied ? " preview-btn--success" : ""}`}
                    onClick={copyShare} title="Copiar URL compartible">
              <Copy size={14} />
              <span>{copied ? "¡Copiado!" : "Share"}</span>
            </button>
          )}

          {/* Open in new tab */}
          {canInteract && (
            <button className="preview-btn" onClick={openExternal} title="Abrir en nueva pestaña">
              <ExternalLink size={14} />
            </button>
          )}

          {/* Logs toggle */}
          <button className={`preview-btn${logsOpen ? " preview-btn--active" : ""}`}
                  onClick={() => setLogsOpen(o => !o)} title="Logs del proceso">
            {logsOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            <span>Logs</span>
          </button>

          {/* Stop — visible when running */}
          {(isLive || status === "building") && (
            <button className="preview-btn preview-btn--stop"
                    onClick={stopPreview} title="Detener preview">
              <Square size={12} strokeWidth={0} style={{ fill: "currentColor" }} />
              <span>Stop</span>
            </button>
          )}

          {/* Close */}
          {onClose && (
            <button className="preview-btn preview-btn--danger" onClick={onClose} title="Cerrar">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── URL bar — visible when live ─────────────────────────────────── */}
      {(proxyUrl || status === "starting") && (
        <div className="preview-url-bar">
          <span className={`preview-url-dot ${dotClass}`} />
          <span className={`preview-url-text${isLive ? " preview-url-text--live" : ""}`}>
            {proxyUrl ? proxyUrl : "iniciando..."}
          </span>
        </div>
      )}

      {/* ── Iframe area ─────────────────────────────────────────────────── */}
      <div className="preview-iframe-wrap" style={{ flex: 1 }}>
        {(status === "idle" || status === "stopped") && (
          <div className="preview-empty">
            <Monitor size={40} strokeWidth={1} style={{ opacity: 0.15, marginBottom: "1rem" }} />
            <p style={{ fontWeight: 600, opacity: 0.7 }}>
              {status === "stopped" ? "Preview detenido" : "Preview no iniciado"}
            </p>
            <button className="cta-primary" onClick={startPreview} style={{ marginTop: "1rem" }}>
              ▶ {status === "stopped" ? "Reiniciar preview" : "Iniciar preview"}
            </button>
          </div>
        )}

        {(status === "starting") && (
          /* P5: rich startup progress with phase label and bar */
          <div className="preview-starting">
            <div className="preview-starting-icon">
              <Loader2 size={32} strokeWidth={1.5} style={{ animation: "spin 1s linear infinite", color: "#D97706" }} />
            </div>
            <p className="preview-starting-phase">{startPhase || "Iniciando..."}</p>
            <div className="preview-starting-bar-wrap">
              <div
                className="preview-starting-bar-fill"
                style={{ width: `${startPct}%`, transition: startPct > 5 ? "width 0.6s ease" : "none" }}
              />
            </div>
            <p className="preview-starting-hint">
              {startPct < 30 ? "Preparando el entorno virtual..." :
               startPct < 65 ? "Instalando dependencias (~30s la primera vez)..." :
               startPct < 90 ? "Arrancando el servidor..." :
               "Casi listo..."}
            </p>
          </div>
        )}

        {(status === "crashed" || status === "error") && (
          <div className="preview-empty preview-empty--error">
            <XCircle size={36} strokeWidth={1.5} style={{ color: "#DC2626", opacity: 0.6, marginBottom: "1rem" }} />
            <p style={{ fontWeight: 600 }}>Preview falló</p>
            {error && <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", marginTop: "0.4rem", maxWidth: 340 }}>{error}</p>}
            <button className="cta-primary" onClick={startPreview} style={{ marginTop: "1rem" }}>Reintentar</button>
            <button className="cta-secondary" onClick={() => setLogsOpen(true)} style={{ marginTop: "0.4rem" }}>Ver logs</button>
          </div>
        )}

        {canInteract && proxyUrl && (
          mobile ? (
            /* ── Phone bezel mode ────────────────────────────────────────── */
            <div className="preview-phone-frame">
              <div className="preview-phone-inner" style={{ position: "relative" }}>
                {/* P2: live-reload progress bar — no white flash */}
                {reloading && <div className="preview-reload-bar" />}
                {/* P2: file-change toast */}
                {reloadToast && (
                  <div className="preview-reload-toast">{reloadToast}</div>
                )}
                {/* P4: friendly error overlay when iframe loads JSON/blank */}
                {iframeError && (
                  <div className="preview-iframe-error-overlay">
                    <XCircle size={28} strokeWidth={1.5} style={{ color: "#f87171", marginBottom: "0.6rem" }} />
                    <p className="preview-iframe-error-msg">{iframeError}</p>
                    <button className="cta-primary" style={{ marginTop: "0.75rem", fontSize: "0.8rem", padding: "0.4rem 0.9rem" }}
                            onClick={() => { setIframeError(""); setReloadKey(k => k+1); setIframeReady(false); }}>
                      Reintentar
                    </button>
                    <button style={{ marginTop: "0.4rem", fontSize: "0.75rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}
                            onClick={() => setIframeError("")}>
                      Ignorar
                    </button>
                  </div>
                )}
                {!iframeReady && !reloading && (
                  <div className="preview-iframe-loading">
                    <Loader2 size={20} strokeWidth={1.5} style={{ animation: "spin 1s linear infinite" }} />
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  key={reloadKey}
                  src={shareUrl || proxyUrl}
                  title={`Preview: ${appSlug}`}
                  className="preview-iframe"
                  style={{ opacity: iframeReady ? 1 : 0.3, transition: "opacity 0.3s" }}
                  onLoad={(e) => { setIframeReady(true); setReloading(false); probeIframe(e.target.src); }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
              </div>
            </div>
          ) : (
            /* ── Desktop full-width ──────────────────────────────────────── */
            <div className="preview-iframe-container" style={{ position: "relative", height: "100%" }}>
              {/* P2: live-reload progress bar — no white flash */}
              {reloading && <div className="preview-reload-bar" />}
              {/* P2: file-change toast */}
              {reloadToast && (
                <div className="preview-reload-toast">{reloadToast}</div>
              )}
              {/* P4: friendly error overlay when iframe loads JSON/blank */}
              {iframeError && (
                <div className="preview-iframe-error-overlay">
                  <XCircle size={28} strokeWidth={1.5} style={{ color: "#f87171", marginBottom: "0.6rem" }} />
                  <p className="preview-iframe-error-msg">{iframeError}</p>
                  <button className="cta-primary" style={{ marginTop: "0.75rem", fontSize: "0.8rem", padding: "0.4rem 0.9rem" }}
                          onClick={() => { setIframeError(""); setReloadKey(k => k+1); setIframeReady(false); }}>
                    Reintentar
                  </button>
                  <button style={{ marginTop: "0.4rem", fontSize: "0.75rem", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}
                          onClick={() => setIframeError("")}>
                    Ignorar
                  </button>
                </div>
              )}
              {!iframeReady && !reloading && (
                <div className="preview-iframe-loading">
                  <Loader2 size={20} strokeWidth={1.5} style={{ animation: "spin 1s linear infinite" }} />
                </div>
              )}
              <iframe
                ref={iframeRef}
                key={reloadKey}
                src={shareUrl || proxyUrl}
                title={`Preview: ${appSlug}`}
                className="preview-iframe"
                style={{ opacity: iframeReady ? 1 : 0.3, transition: "opacity 0.3s" }}
                onLoad={() => { setIframeReady(true); setReloading(false); }}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
              />
            </div>
          )
        )}
      </div>

      {/* ── Logs panel ──────────────────────────────────────────────────── */}
      {logsOpen && (
        <div className="preview-logs">
          <div className="preview-logs-header">
            <span>Logs del proceso</span>
            <button className="preview-btn" onClick={() => setLogs([])}>Limpiar</button>
          </div>
          <div className="preview-logs-body">
            {logs.length === 0 && (
              <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Sin logs aún...</span>
            )}
            {logs.map((line, i) => (
              <div key={i} className={`preview-log-line${line.startsWith("✗") ? " preview-log-error" : line.startsWith("✓") ? " preview-log-ok" : ""}`}>
                {line}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}
